import { useEffect, useRef, useState } from 'react';

// Generic, FlashRoom-agnostic native-OS-file-drag detector. Distinguishes a
// real "drag a file/folder in from the desktop" gesture from the app's own
// internal drag-and-drop (moving an existing snippet/file/folder card around,
// see shared/utils/driveDnd.ts) purely by whether the browser reports a
// 'Files' entry in DataTransfer.types — the app's internal drags never set
// that, only OS file drags do, so the two can never collide.

export type DroppedFilesHandler = (
    files: File[],
    point: { clientX: number; clientY: number }
) => void;

type UseOSFileDropOptions = {
    containerRef: React.RefObject<HTMLElement | null>;
    enabled: boolean;
    onDrop: DroppedFilesHandler;
    // Fired continuously while an OS file drag is over the container, for
    // callers that need to resolve/highlight a drop target under the cursor
    // (e.g. a folder widget) in real time.
    onDragOverPoint?: (point: { clientX: number; clientY: number }) => void;
};

// Minimal local typing for the (non-standard-lib) File System Entry API used
// to walk dropped directories — only the pieces actually used here.
interface FileSystemEntryLike {
    isFile: boolean;
    isDirectory: boolean;
    name: string;
}
interface FileSystemFileEntryLike extends FileSystemEntryLike {
    file: (success: (file: File) => void, error?: (err: unknown) => void) => void;
}
interface FileSystemDirectoryEntryLike extends FileSystemEntryLike {
    createReader: () => {
        readEntries: (
            success: (entries: FileSystemEntryLike[]) => void,
            error?: (err: unknown) => void
        ) => void;
    };
}

function readEntryFile(entry: FileSystemFileEntryLike): Promise<File> {
    return new Promise((resolve, reject) => entry.file(resolve, reject));
}

// FileSystemDirectoryReader.readEntries() is not guaranteed to return every
// child in one call (spec limitation) — it must be called repeatedly until it
// returns an empty array.
function readAllDirectoryEntries(dirEntry: FileSystemDirectoryEntryLike): Promise<FileSystemEntryLike[]> {
    const reader = dirEntry.createReader();
    const all: FileSystemEntryLike[] = [];

    const readBatch = (): Promise<void> =>
        new Promise((resolve, reject) => {
            reader.readEntries((entries) => {
                if (!entries.length) {
                    resolve();
                    return;
                }
                all.push(...entries);
                readBatch().then(resolve).catch(reject);
            }, reject);
        });

    return readBatch().then(() => all);
}

// Attaches a webkitRelativePath-equivalent onto a plain File so every
// existing helper that already reads File.webkitRelativePath
// (getFolderPaths/hasExceededFolderDepth in folderUploadLimits.ts,
// uploadFolderTree in UploadEngineContext.tsx) works completely unchanged for
// a dropped file, exactly as it already does for a native
// <input webkitdirectory> selection. A loose top-level file gets '' (matches
// what a plain multi-file <input> produces), so it naturally resolves to the
// upload's target folder instead of being treated as part of a folder path.
function withRelativePath(file: File, path: string): File {
    if (path) {
        try {
            Object.defineProperty(file, 'webkitRelativePath', { value: path, configurable: true });
        } catch {
            // Some environments may not allow overriding a File instance's
            // property; the file still uploads, just without folder placement.
        }
    }
    return file;
}

async function walkEntry(entry: FileSystemEntryLike, pathPrefix: string, out: File[]): Promise<void> {
    if (entry.isFile) {
        const file = await readEntryFile(entry as FileSystemFileEntryLike);
        out.push(withRelativePath(file, pathPrefix ? `${pathPrefix}/${entry.name}` : ''));
        return;
    }

    if (entry.isDirectory) {
        const nextPrefix = pathPrefix ? `${pathPrefix}/${entry.name}` : entry.name;
        const children = await readAllDirectoryEntries(entry as FileSystemDirectoryEntryLike);
        for (const child of children) {
            await walkEntry(child, nextPrefix, out);
        }
    }
}

async function collectDroppedFiles(dataTransfer: DataTransfer): Promise<File[]> {
    const items = Array.from(dataTransfer.items || []);
    const entries = items
        .map((item) => {
            const getAsEntry = (item as unknown as { webkitGetAsEntry?: () => FileSystemEntryLike | null }).webkitGetAsEntry;
            return typeof getAsEntry === 'function' ? getAsEntry.call(item) : null;
        })
        .filter((e): e is FileSystemEntryLike => !!e);

    if (entries.length) {
        const out: File[] = [];
        for (const entry of entries) {
            await walkEntry(entry, '', out);
        }
        return out;
    }

    // Fallback for browsers without webkitGetAsEntry support: flat files only,
    // no folder-structure support.
    return Array.from(dataTransfer.files || []);
}

export function useOSFileDrop({ containerRef, enabled, onDrop, onDragOverPoint }: UseOSFileDropOptions) {
    const [isDraggingFiles, setIsDraggingFiles] = useState(false);
    const dragDepthRef = useRef(0);
    const enabledRef = useRef(enabled);
    const onDropRef = useRef(onDrop);
    const onDragOverPointRef = useRef(onDragOverPoint);
    enabledRef.current = enabled;
    onDropRef.current = onDrop;
    onDragOverPointRef.current = onDragOverPoint;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const isOSFileDrag = (e: DragEvent) =>
            !!e.dataTransfer && Array.from(e.dataTransfer.types || []).includes('Files');

        const handleDragEnter = (e: DragEvent) => {
            if (!enabledRef.current || !isOSFileDrag(e)) return;
            e.preventDefault();
            dragDepthRef.current += 1;
            setIsDraggingFiles(true);
        };

        const handleDragOver = (e: DragEvent) => {
            if (!enabledRef.current || !isOSFileDrag(e)) return;
            e.preventDefault();
            if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
            onDragOverPointRef.current?.({ clientX: e.clientX, clientY: e.clientY });
        };

        const handleDragLeave = (e: DragEvent) => {
            if (!enabledRef.current || !isOSFileDrag(e)) return;
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
            if (dragDepthRef.current === 0) setIsDraggingFiles(false);
        };

        const handleDrop = (e: DragEvent) => {
            if (!enabledRef.current || !isOSFileDrag(e) || !e.dataTransfer) return;
            e.preventDefault();
            e.stopPropagation();
            dragDepthRef.current = 0;
            setIsDraggingFiles(false);

            const point = { clientX: e.clientX, clientY: e.clientY };
            const dataTransfer = e.dataTransfer;
            void collectDroppedFiles(dataTransfer).then((files) => {
                if (files.length) onDropRef.current(files, point);
            });
        };

        // Capture phase: claim genuine OS file drags before any nested bubble-
        // phase handler (Monaco's own built-in drop handling included) can see
        // or swallow them. Internal app-to-app drag-and-drop (never carries a
        // 'Files' type) is left completely untouched by every handler above.
        container.addEventListener('dragenter', handleDragEnter, true);
        container.addEventListener('dragover', handleDragOver, true);
        container.addEventListener('dragleave', handleDragLeave, true);
        container.addEventListener('drop', handleDrop, true);

        return () => {
            container.removeEventListener('dragenter', handleDragEnter, true);
            container.removeEventListener('dragover', handleDragOver, true);
            container.removeEventListener('dragleave', handleDragLeave, true);
            container.removeEventListener('drop', handleDrop, true);
        };
        // enabled is read via enabledRef inside the handlers above (not a dep
        // here) so toggling it doesn't tear down and re-attach the listeners.
    }, [containerRef]);

    return { isDraggingFiles };
}
